import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, Link } from 'react-router-dom';
import { ClockIcon } from '@heroicons/react/24/outline';
import { searchHealthHacks, HealthHack } from '../services/api';
import NewsletterSignup from '../components/NewsletterSignup';
import { useScrollToTop } from '../hooks/useScrollToTop';

const SearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [searchResults, setSearchResults] = useState<HealthHack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Scroll to top when search query changes
  useScrollToTop();

  useEffect(() => {
    const performSearch = async () => {
      const query = searchParams.get('q');
      
      if (!query || query.trim() === '') {
        setSearchResults([]);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const results = await searchHealthHacks(query.trim());
        setSearchResults(results);
      } catch (err) {
        setError('Failed to perform search');
        console.error('Error searching daily tips:', err);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [searchParams]);

  const getCategoryName = (categoryId: string) => {
    const categories = {
      'stress-relief': 'Stress Relief',
      mindfulness: 'Mindfulness', 
      sleep: 'Sleep Better',
      'self-care': 'Self Care',
      wellness: 'Wellness'
    };
    return categories[categoryId as keyof typeof categories] || categoryId;
  };

  const getCategoryColor = (category: string) => {
    // Use consistent blue for all categories
    return 'category-badge';
  };

  const query = searchParams.get('q') || '';

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Searching... - Destress Daily</title>
        </Helmet>
        
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Searching daily tips...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Search Results for "{query}" - Destress Daily</title>
        <meta name="description" content={`Search results for "${query}" - Find daily tips and wellness advice.`} />
        <meta name="keywords" content={`search, ${query}, wellness tips, wellness, stress relief, mindfulness`} />
        <meta property="og:title" content={`Search Results for "${query}" - Destress Daily`} />
        <meta property="og:description" content={`Search results for "${query}" - Find daily tips and wellness advice.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://dailyhealthhacks.com/search?q=${encodeURIComponent(query)}`} />
        <link rel="canonical" href={`https://dailyhealthhacks.com/search?q=${encodeURIComponent(query)}`} />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-health-50 via-white to-primary-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Search Results
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {query ? `Searching for "${query}"` : 'Enter a search term to find daily tips'}
            </p>
          </div>
        </div>
      </section>

      {/* Search Results */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Search Results for "{query}"
            </h2>
            <p className="text-lg text-gray-600">
              {searchResults.length} daily tip{searchResults.length !== 1 ? 's' : ''} found
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-center py-12">
              <h3 className="text-xl font-bold text-red-600 mb-4">Search Error</h3>
              <p className="text-gray-600 mb-8">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="btn-primary"
              >
                Try Again
              </button>
            </div>
          )}

          {/* No Query */}
          {!query && (
            <div className="text-center py-12">
              <h3 className="text-xl font-bold text-gray-900 mb-4">No Search Query</h3>
              <p className="text-gray-600 mb-8">
                Please enter a search term to find daily tips.
              </p>
              <Link to="/tips" className="btn-primary">
                Browse All Daily Tips
              </Link>
            </div>
          )}

          {/* No Results */}
          {query && !error && searchResults.length === 0 && (
            <div className="text-center py-12">
              <h3 className="text-xl font-bold text-gray-900 mb-4">No Results Found</h3>
              <p className="text-gray-600 mb-8">
                No daily tips found for "{query}". Try different keywords or browse all daily tips.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/tips" className="btn-primary">
                  Browse All Daily Tips
                </Link>
                <Link to="/" className="btn-secondary">
                  Back to Home
                </Link>
              </div>
            </div>
          )}

          {/* Search Results Grid */}
          {query && !error && searchResults.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {searchResults.map((hack) => (
                <article key={hack.id} className="health-hack-card group">
                  <div className="mb-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(hack.category)}`}>
                      {getCategoryName(hack.category)}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                    <Link to={`/tip/${hack.slug}`}>
                      {hack.title}
                    </Link>
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {hack.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {hack.read_time} min read
                    </div>
                    <Link
                      to={`/tip/${hack.slug}`}
                      className="text-primary-600 font-medium hover:text-primary-700 transition-colors duration-200"
                    >
                      Read More →
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <NewsletterSignup variant="hero" />
    </>
  );
};

export default SearchPage; 